// Apolo Planning Mode — PlanState: the plan + ordered TODOs + phase. PURE
// (no React/IPC/fetch). Complements LoopRunState (which tracks engine steps); this
// tracks the user-facing plan a human approves and watches advance.
//
// Phases: planning → awaiting_approval → executing → done.
// Human approval is a HARD gate between awaiting_approval and executing — no mutation
// (no todo advances) before `approve`. Todo completion is decided by the OBJECTIVE
// GATE (gate.js isStepComplete), never by the agent's say-so.

import { isStepComplete } from './gate.js';
import { canComplete, isStop } from '../outcome/runOutcome.js';
import { demoteExtraActives } from './todoDiscipline.js';

/**
 * @typedef {{id:string, text:string, status:'pending'|'active'|'done'|'blocked', progress?: {step: number|null, tool: string|null}|undefined}} Todo
 * @typedef {{planId:string, title:string, todos:Todo[], currentTodoId:string|null, phase:'planning'|'awaiting_approval'|'executing'|'done', gate:{lastVerdict:('pass'|'fail'|'pending'|null), evidence:string[]}, executingSince:number|null, doneAt:number|null, outcome?: import('../outcome/runOutcome.js').RunOutcome}} PlanState
 */

/**
 * P1 — gate de conclusão pelo RunOutcome canônico. Só verified/implemented_unverified
 * CONCLUEM (o outcome fica anexado pra UI diferenciar verde de "não verificado"); blocked
 * e environment_unavailable PAUSAM (segue executing, nunca 'done'); failed bloqueia o todo.
 * @param {PlanState} state
 * @param {import('../outcome/runOutcome.js').RunOutcome} outcome
 * @param {number=} at
 * @returns {PlanState}
 */
function applyRunOutcome(state, outcome, at) {
  const doneAt = Number.isFinite(at) ? at : null;
  if (canComplete(outcome)) {
    return {
      ...state,
      todos: state.todos.map((t) => (t.status === 'done' ? t : { ...t, status: 'done' })),
      currentTodoId: null,
      phase: 'done',
      gate: { lastVerdict: 'pass', evidence: [outcome.detail || outcome.state] },
      doneAt,
      outcome,
    };
  }
  if (isStop(outcome)) {
    // blocked / environment_unavailable → NÃO conclui; fica executing, registra o bloqueio.
    return { ...state, gate: { lastVerdict: 'pending', evidence: [outcome.detail || outcome.reason || outcome.state] }, outcome };
  }
  // failed → bloqueia o todo ativo (retomável), segue executing.
  const id = state.currentTodoId;
  return {
    ...state,
    todos: state.todos.map((t) => (t.id === id && t.status === 'active' ? { ...t, status: 'blocked' } : t)),
    gate: { lastVerdict: 'fail', evidence: [outcome.detail || outcome.reason || 'run failed'] },
    outcome,
  };
}

const TODO_STATUSES = ['pending', 'active', 'done', 'blocked'];

/** @returns {PlanState} */
export function initPlanState(planId = 'plan') {
  // executingSince/doneAt (pill de tempo): carimbos vêm do EVENTO (`event.at`,
  // carimbado no dispatch pelo caller) — o reducer segue PURO. null = pill oculta.
  return { planId: String(planId), title: '', todos: [], currentTodoId: null, phase: 'planning', gate: { lastVerdict: null, evidence: [] }, executingSince: null, doneAt: null };
}

function normalizeTodos(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((t, i) => {
      if (typeof t === 'string') return { id: `t${i + 1}`, text: t, status: 'pending' };
      if (t && typeof t === 'object' && typeof t.text === 'string') {
        return {
          id: String(t.id || `t${i + 1}`),
          text: t.text,
          status: TODO_STATUSES.includes(t.status) ? t.status : 'pending',
        };
      }
      return null;
    })
    .filter(Boolean);
}

/** First `pending` todo (in order), or null. */
export function nextTodo(state) {
  return state?.todos?.find((t) => t.status === 'pending') || null;
}

// RCA lentidão fix I — todo saindo de cena (done/blocked/pending) solta o progresso
// vivo; só 'active' o mantém (o próximo todo_progress repõe).
/** @param {Todo} t @param {Todo['status']} status @returns {Todo} */
const withTodoStatus = (t, status) => (status === 'active' ? { ...t, status } : { ...t, status, progress: undefined });

// RCA lentidão fix I — progresso VIVO dentro de uma task: o loop emite loop_step/
// tool_call e o bridge vira {step, tool} no todo. Antes uma task de 40min era um
// spinner mudo; agora a linha mostra "passo N · tool". Só em executing; id
// desconhecido = no-op (mesma disciplina do todo_status). Merge incremental: step
// e tool chegam em eventos separados e o mais novo preenche sem apagar o outro.
function applyTodoProgress(state, event) {
  if (state.phase !== 'executing') return state;
  const id = event.id;
  const target = id ? state.todos.find((t) => t.id === id) : null;
  if (!target) return state;
  const prev = target.progress || {};
  const progress = {
    step: Number.isFinite(Number(event.step)) ? Number(event.step) : (prev.step ?? null),
    tool: typeof event.tool === 'string' && event.tool ? event.tool : (prev.tool ?? null),
  };
  return { ...state, todos: state.todos.map((t) => (t.id === id ? { ...t, progress } : t)) };
}

// Agent-driven PROGRESS report (live UX): marca um todo active/done/blocked enquanto o
// agente trabalha o plano aprovado. Corpo extraído do reducePlan (ratchet de complexidade,
// como applyTodoProgress). A completude AUTORITATIVA do plano ainda vem do run_result.
// harness-audit 07-13 — disciplina rule #2 (um active) via demoteExtraActives: ativar um
// todo demote qualquer OUTRO active (antes o reducer deixava múltiplos). Não dropa nada.
function applyTodoStatus(state, event) {
  if (state.phase !== 'executing') return state;
  const id = event.id || state.currentTodoId;
  const status = TODO_STATUSES.includes(event.status) ? event.status : null;
  if (!id || !status) return state;
  const mapped = state.todos.map((t) => (t.id === id ? withTodoStatus(t, status) : t));
  const todos = status === 'active' ? demoteExtraActives(mapped, id) : mapped;
  // advance:false (eventos do executor DAG) — só o task_started REAL ativa o próximo todo;
  // o auto-advance abaixo é pro caminho agent-driven (update_todo).
  if (status === 'done' && event.advance !== false) {
    const upcoming = todos.find((t) => t.status === 'pending');
    const todos2 = upcoming ? demoteExtraActives(todos.map((t) => (t.id === upcoming.id ? { ...t, status: 'active' } : t)), upcoming.id) : todos;
    return { ...state, todos: todos2, currentTodoId: upcoming?.id || null };
  }
  return { ...state, todos, currentTodoId: status === 'active' ? id : state.currentTodoId };
}

/**
 * The ONLY transition function. Immutable; unknown events / illegal transitions are
 * no-ops (same ref). Enforces: no advance before approval; gate decides completion.
 * @param {PlanState} state @param {object} event @returns {PlanState}
 */
export function reducePlan(state, event) {
  if (!state || typeof state !== 'object' || !event || typeof event !== 'object') return state;
  switch (event.type) {
    case 'propose': {
      const todos = normalizeTodos(event.todos);
      return { ...state, title: String(event.title || state.title || ''), todos, phase: 'awaiting_approval', currentTodoId: null };
    }
    case 'edit': {
      // (T5) Revise the PROPOSED todos in place WITHOUT leaving the approval gate —
      // a human tweaks the plan (reorder / reword / drop steps) before approving.
      // Only valid from awaiting_approval; never advances a todo (gate untouched).
      if (state.phase !== 'awaiting_approval') return state;
      const todos = normalizeTodos(event.todos);
      return { ...state, title: String(event.title ?? state.title ?? ''), todos };
    }
    case 'reject':
      // back to planning; the agent revises. Plan log kept (caller persists separately).
      return state.phase === 'awaiting_approval' ? { ...state, phase: 'planning' } : state;
    case 'approve': {
      if (state.phase !== 'awaiting_approval') return state; // approval only from the gate
      const first = nextTodo(state);
      const todos = first ? state.todos.map((t) => (t.id === first.id ? { ...t, status: 'active' } : t)) : state.todos;
      return { ...state, phase: 'executing', currentTodoId: first?.id || null, todos, executingSince: Number.isFinite(event.at) ? event.at : null, doneAt: null };
    }
    case 'gate': {
      // The objective gate's verdict for the current/identified todo. PASS → done +
      // advance; FAIL → blocked; PENDING → stays active. No model text is consulted.
      if (state.phase !== 'executing') return state;
      const id = event.todoId || state.currentTodoId;
      if (!id) return state;
      const verdict = event.verdict; // {verdict, evidence}
      const pass = isStepComplete(verdict);
      const blocked = verdict?.verdict === 'fail';
      const todos = state.todos.map((t) => {
        if (t.id !== id) return t;
        return { ...t, status: pass ? 'done' : blocked ? 'blocked' : 'active' };
      });
      let next = state;
      const base = { ...state, todos, gate: { lastVerdict: verdict?.verdict ?? 'pending', evidence: Array.isArray(verdict?.evidence) ? verdict.evidence.slice(0, 10) : [] } };
      if (pass) {
        const upcoming = nextTodo(base);
        const todos2 = upcoming ? base.todos.map((t) => (t.id === upcoming.id ? { ...t, status: 'active' } : t)) : base.todos;
        next = { ...base, todos: todos2, currentTodoId: upcoming?.id || null, phase: upcoming ? 'executing' : 'done', doneAt: upcoming ? base.doneAt : (Number.isFinite(event.at) ? event.at : null) };
      } else {
        next = base;
      }
      return next;
    }
    case 'todo_progress':
      // RCA lentidão fix I — progresso VIVO dentro de uma task ("passo N · tool");
      // corpo extraído (applyTodoProgress) pelo ratchet de complexidade.
      return applyTodoProgress(state, event);
    case 'todo_status':
      return applyTodoStatus(state, event);
    case 'run_result': {
      // OBJECTIVE plan-completion signal from the run lifecycle (not the agent's word).
      // Apolo runs the whole task as one multi-turn run, so a clean finish = the
      // approved plan was delivered → mark every not-done todo done + phase done.
      // A failed run blocks the current todo (phase stays executing so the user can retry).
      if (state.phase !== 'executing') return state;
      // P1 — RunOutcome canônico (preferido). Sem outcome → caminho legado {ok} abaixo (byte-idêntico).
      if (event.outcome && event.outcome.state) return applyRunOutcome(state, event.outcome, event.at);
      if (event.ok === true) {
        return {
          ...state,
          todos: state.todos.map((t) => (t.status === 'done' ? t : { ...t, status: 'done' })),
          currentTodoId: null,
          phase: 'done',
          gate: { lastVerdict: 'pass', evidence: ['run completed successfully'] },
          doneAt: Number.isFinite(event.at) ? event.at : null,
        };
      }
      const id = state.currentTodoId;
      return {
        ...state,
        todos: state.todos.map((t) => (t.id === id && t.status === 'active' ? { ...t, status: 'blocked' } : t)),
        gate: { lastVerdict: 'fail', evidence: ['run ended without success'] },
      };
    }
    default:
      return state;
  }
}

export const _internals = { TODO_STATUSES, normalizeTodos };
